import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Stat, Button, Input, Select, Field } from "@/components/UI";
import { Baby, Robot, Package, Plus, CalendarCheck, TrendDown, Truck, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CalfBoard } from "./calf/CalfBoard";


export default function CalfFeeding() {
  const { user } = useAuth();
  const isVet = user?.role === "vet";
  const [stockItems, setStockItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [feedForecast, setFeedForecast] = useState(null);
  const [mode, setMode] = useState("manual"); // "manual" | "lely_calm"
  const [showBatch, setShowBatch] = useState(false);
  const [savingBatch, setSavingBatch] = useState(false);
  const EMPTY_BATCH = { stock_id: "", amount: "", notes: "" };
  const [batch, setBatch] = useState(EMPTY_BATCH);

  const loadStock = useCallback(() => {
    api.get("/calf/feed-items").then(r => setStockItems(r.data || [])).catch(() => {});
  }, []);
  const loadSummary = useCallback(() => api.get("/calf/summary").then(r => setSummary(r.data)).catch(() => {}), []);
  const loadForecast = useCallback(() => api.get("/calf/feed-forecast?days=14").then(r => setFeedForecast(r.data)).catch(() => {}), []);
  useEffect(() => { loadStock(); loadSummary(); loadForecast(); }, [loadStock, loadSummary, loadForecast]);

  const fmt = (v) => Number(v || 0).toLocaleString("tr-TR");
  const refresh = () => { loadSummary(); loadStock(); loadForecast(); };

  const submitBatch = async (e) => {
    e.preventDefault();
    if (!batch.stock_id) return toast.error("Mama kalemini seçin");
    if (!batch.amount || Number(batch.amount) <= 0) return toast.error("Geçerli bir miktar girin");
    setSavingBatch(true);
    try {
      const r = await api.post("/calf/feed-batch", {
        stock_id: batch.stock_id, amount: Number(batch.amount), notes: batch.notes || null,
      });
      const d = r.data;
      toast.success(`${d.name}: ${fmt(d.added)} ${d.unit} eklendi · yeni stok ${fmt(d.new_quantity)} ${d.unit}`);
      setBatch(EMPTY_BATCH); setShowBatch(false); refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Parti eklenemedi");
    } finally { setSavingBatch(false); }
  };

  const ff = feedForecast;
  const critical = !!ff?.critical;

  return (
    <div data-testid="calf-page">
      <PageHeader
        title="Buzağı Besleme"
        subtitle="Mama & ağız sütü tüketimi — haftalık çizelge kartları, hafta onayı/kilit, aylık arşiv ve gerçek tüketim raporlaması."
      />

      {/* ---- MAMA STOK ÖZET KARTI (iPad dostu) ---- */}
      <Card
        testId="calf-feed-forecast-card"
        className={`mb-4 ${critical ? "!bg-[#FBECEA] !border-[#E9B5AB]" : "!bg-[#F0F6F1] !border-[#CFE1D4]"}`}
      >
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${critical ? "bg-[#F5D5CE]" : "bg-[#DCEBE0]"}`}>
              <Package size={26} weight="duotone" className={critical ? "text-[#8B2E1D]" : "text-[#2D4A36]"} />
            </div>
            <div>
              <h3 className={`heading text-lg sm:text-xl ${critical ? "text-[#8B2E1D]" : "text-[#1f3326]"}`}>Mama Stok Durumu</h3>
              <p className="text-sm text-[#5e675e]">Son 14 günlük tüketime göre öngörü · {ff?.feed_items_count ?? 0} mama kalemi</p>
            </div>
          </div>
          {critical && (
            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-white bg-[#C45B45] px-3 py-1.5 rounded-full" data-testid="calf-feed-critical-badge">
              <Warning size={16} weight="fill" /> Kritik — Sipariş Zamanı
            </span>
          )}
          {isVet && (
            <Button data-testid="calf-batch-toggle" onClick={() => setShowBatch(v => !v)}>
              <Plus size={16} className="inline mr-1" /> {showBatch ? "Kapat" : "Yeni Parti Ekle"}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="calf-feed-forecast-grid">
          <div className="bg-white/70 rounded-xl p-4 border border-black/5">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#8a938c] mb-1">Mevcut Stok</div>
            <div className="text-2xl sm:text-3xl font-bold tabnum text-[#1f3326]" data-testid="calf-feed-stock-kg">
              {fmt(ff?.total_stock_kg)} <span className="text-base font-semibold text-[#8a938c]">kg</span>
            </div>
          </div>
          <div className="bg-white/70 rounded-xl p-4 border border-black/5">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#8a938c] mb-1 flex items-center gap-1"><TrendDown size={13} weight="bold" /> Günlük Tüketim</div>
            <div className="text-2xl sm:text-3xl font-bold tabnum text-[#8B6914]" data-testid="calf-feed-daily-avg">
              {ff?.daily_avg_kg > 0 ? fmt(ff.daily_avg_kg) : "—"} <span className="text-base font-semibold text-[#8a938c]">kg/gün</span>
            </div>
          </div>
          <div className="bg-white/70 rounded-xl p-4 border border-black/5">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#8a938c] mb-1 flex items-center gap-1"><CalendarCheck size={13} weight="bold" /> Tahmini Bitiş</div>
            <div className="text-xl sm:text-2xl font-bold tabnum text-[#1f3326]" data-testid="calf-feed-empty-date">
              {ff?.predicted_empty_date || "—"}
            </div>
            {ff?.days_remaining != null && (
              <div className={`text-xs font-semibold mt-0.5 ${critical ? "text-[#C45B45]" : "text-[#5e675e]"}`}>~{fmt(ff.days_remaining)} gün kaldı</div>
            )}
          </div>
          <div className={`rounded-xl p-4 border ${critical ? "bg-[#C45B45] border-[#C45B45] text-white" : "bg-white/70 border-black/5"}`}>
            <div className={`text-xs font-semibold uppercase tracking-wider mb-1 flex items-center gap-1 ${critical ? "text-white/80" : "text-[#8a938c]"}`}><Truck size={13} weight="bold" /> Önerilen Sipariş</div>
            <div className={`text-xl sm:text-2xl font-bold tabnum ${critical ? "text-white" : "text-[#2D4A36]"}`} data-testid="calf-feed-reorder-date">
              {ff?.reorder_date || "—"}
            </div>
            <div className={`text-xs mt-0.5 ${critical ? "text-white/80" : "text-[#8a938c]"}`}>tedarik {ff?.lead_time_days ?? 3} gün</div>
          </div>
        </div>

        {isVet && showBatch && (
          <form onSubmit={submitBatch} className="mt-4 pt-4 border-t border-black/10 grid sm:grid-cols-4 gap-3" data-testid="calf-batch-form">
            <div className="sm:col-span-2">
              <Field label="Mama Kalemi *">
                <Select required data-testid="calf-batch-stock" value={batch.stock_id} onChange={e => setBatch({ ...batch, stock_id: e.target.value })}>
                  <option value="">Seçin...</option>
                  {stockItems.map(s => (
                    <option key={s.id} value={s.id}>{s.name} · {s.category || "—"} (mevcut {fmt(s.quantity)} {s.unit})</option>
                  ))}
                </Select>
              </Field>
            </div>
            <Field label="Eklenecek Miktar *">
              <div className="flex items-center gap-2">
                <Input type="number" step="0.01" min="0" required data-testid="calf-batch-amount"
                  value={batch.amount} onChange={e => setBatch({ ...batch, amount: e.target.value })} placeholder="Örn: 500" />
                <span className="text-xs text-[#8a938c] whitespace-nowrap">
                  {stockItems.find(s => s.id === batch.stock_id)?.unit || "kg"}
                </span>
              </div>
            </Field>
            <Field label="Not">
              <Input data-testid="calf-batch-notes" value={batch.notes} onChange={e => setBatch({ ...batch, notes: e.target.value })} placeholder="opsiyonel (parti no, tedarikçi…)" />
            </Field>
            <div className="sm:col-span-4 flex items-center justify-between gap-2">
              <span className="text-xs text-[#8a938c]">Fiyat/gider etkilenmez — yalnızca stok miktarı artar. Mevcut miktarın üstüne eklenir.</span>
              <div className="flex gap-2">
                <Button type="button" variant="secondary" onClick={() => { setShowBatch(false); setBatch(EMPTY_BATCH); }}>İptal</Button>
                <Button type="submit" disabled={savingBatch} data-testid="calf-batch-submit">{savingBatch ? "Ekleniyor..." : "Partiyi Stoğa Ekle"}</Button>
              </div>
            </div>
          </form>
        )}
      </Card>


      <Card className="mb-4" testId="calf-intro">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-[#FBF1DE] rounded-md">
            <Baby size={22} weight="duotone" className="text-[#8B6914]" />
          </div>
          <p className="text-sm text-[#5e675e] flex-1">
            Her hafta için bir <b>Çizelge Kartı</b> açın. <b>Manuel</b> kartta günlük buzağı sayısını,
            <b> Lely Calm</b> kartında ise günlük dökülen mamayı (kg) girin — her giriş anında stoktan
            düşülür ve finansa <b>"Buzağı Büyütme Gideri"</b> olarak yazılır. Hafta bitince
            <b> "Haftayı Onayla/Kapat"</b> ile kartı mühürleyin; kapatılan kartlar salt-okunur olur
            (kilidi yalnızca yönetici açar). Geçmiş aylar otomatik olarak <b>Aylık Arşiv</b> altında toplanır.
          </p>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="calf-kpis">
        <Stat testId="calf-kpi-cards" label="Toplam Kart" value={summary?.card_count ?? 0} tone="accent" />
        <Stat testId="calf-kpi-kg" label="Toplam Tüketim" value={fmt(summary?.total_kg)} unit="kg" tone="ok" />
        <Stat testId="calf-kpi-cost" label="Buzağı Büyütme Gideri" value={fmt(Math.round(summary?.total_cost || 0))} unit="₺" tone="warn" />
        <Stat testId="calf-kpi-month" label="Bu Ay Gider" value={fmt(Math.round(summary?.this_month_cost || 0))} unit="₺" tone="warn" />
      </div>

      <div className="flex gap-2 mb-4 max-w-xl" data-testid="calf-mode-toggle">
        <button type="button" data-testid="calf-mode-manual" onClick={() => setMode("manual")}
          className={`flex-1 px-3 py-2.5 rounded-md text-sm font-semibold border flex items-center justify-center gap-2 transition-colors ${mode === "manual" ? "bg-[#2D4A36] text-white border-[#2D4A36]" : "bg-white text-[#5e675e] border-[#E5E2DC] hover:border-[#2D4A36]"}`}>
          <Baby size={16} weight="duotone" /> Manuel Besleme
        </button>
        <button type="button" data-testid="calf-mode-lely" onClick={() => setMode("lely_calm")}
          className={`flex-1 px-3 py-2.5 rounded-md text-sm font-semibold border flex items-center justify-center gap-2 transition-colors ${mode === "lely_calm" ? "bg-[#2D4A36] text-white border-[#2D4A36]" : "bg-white text-[#5e675e] border-[#E5E2DC] hover:border-[#2D4A36]"}`}>
          <Robot size={16} weight="duotone" /> Lely Calm
        </button>
      </div>

      <CalfBoard key={mode} mode={mode} stockItems={stockItems} fmt={fmt} onMutate={refresh} />
    </div>
  );
}
